/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import axios from 'axios';
import { apiHeaders } from '../../utils/StoreUtils';
import { Action, ActionCreators } from './actions';
import { Tokens } from '@okta/okta-auth-js';
import { isMockMode } from '../../utils/GlobalUtils';

export interface State {
  readonly tokens?: Tokens;
  readonly authenticated: boolean;
  readonly shouldSignOut: boolean;
}

const initialState: State = {
  authenticated: false,
  shouldSignOut: false,
};

// Load mock tokens from local storage if they exist. This allows devs
// to simulate long-lived logins across page refreshes, or deep-links
// into the app.
if (isMockMode()) {
  const tokens = localStorage.getItem('mockTokens');
  if (tokens) {
    (initialState as any).tokens = JSON.parse(tokens);
    (initialState as any).authenticated = true;
  }
}

export const initialSessionState = initialState;

// Reducer
export default function reducer(state: State = initialSessionState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    case ActionCreators.SetAuthTokens.type:
      partialState = {
        authenticated: true,
        tokens: action.payload.tokens,
      };

      const accessToken = action.payload.tokens.accessToken?.accessToken;
      apiHeaders.Authorization = `Bearer ${accessToken}`;
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      break;

    case ActionCreators.SignOut.type:
      partialState = {
        authenticated: false,
        tokens: undefined,
        shouldSignOut: true,
      };
      delete apiHeaders.Authorization;
      delete axios.defaults.headers.common.Authorization;
      if (isMockMode()) {
        localStorage.removeItem('mockTokens');
      }
      break;

    default:
      return state;
  }

  return { ...state, ...partialState };
}
